<template>
  <div class="orphan-management">
    <!-- Modal de gestion des sections à problèmes -->
    <Teleport to="body">
      <div v-if="sectionsStore.showOrphanModal" class="section-modal-overlay" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="section-modal-container">
          <div class="section-modal-content section-modal-content-xl">
            <div class="section-modal-header">
              <h3 class="section-modal-title">
                🔧 Gestion des sections à problèmes ({{ sectionsStore.problemCount }})
              </h3>
              
              <div class="section-modal-body">
                <!-- État vide -->
                <div v-if="sectionsStore.problemCount === 0" class="section-empty">
                  <div class="section-empty-icon">✅</div>
                  <h4 class="section-empty-title">Aucun problème détecté !</h4>
                  <p class="section-empty-description">Toutes les sections sont correctement configurées.</p>
                </div>
                
                <!-- Liste des problèmes -->
                <div v-else class="section-problems-container">
                  <!-- En-tête informatif -->
                  <div class="section-problems-header">
                    <p class="section-problems-text">
                      <strong>Problèmes détectés :</strong> Ces sections nécessitent votre attention pour fonctionner correctement.
                    </p>
                  </div>
                  
                  <!-- Liste des sections à problèmes -->
                  <div class="section-problems-list">
                    <div class="section-problems-grid">
                      <div 
                        v-for="problem in sectionsStore.problemSections" 
                        :key="`${problem.section.id}-${problem.problemType}`"
                        class="section-problem-card"
                        :class="{
                          'section-problem-card-orphaned': problem.problemType === 'orphaned',
                          'section-problem-card-no-type': problem.problemType === 'no-type',
                          'section-problem-card-broken-condition': problem.problemType === 'broken-condition'
                        }"
                      >
                        <div class="flex flex-col space-y-3">
                          <!-- En-tête de la carte -->
                          <div class="flex-1">
                            <h4 
                              class="section-problem-title"
                              :class="{
                                'section-problem-title-orphaned': problem.problemType === 'orphaned',
                                'section-problem-title-no-type': problem.problemType === 'no-type',
                                'section-problem-title-broken-condition': problem.problemType === 'broken-condition'
                              }"
                            >
                              {{ problem.section.name }}
                            </h4>
                            
                            <!-- Description du problème -->
                            <div class="space-y-2">
                              <p 
                                class="section-problem-description"
                                :class="{
                                  'section-problem-description-orphaned': problem.problemType === 'orphaned',
                                  'section-problem-description-no-type': problem.problemType === 'no-type',
                                  'section-problem-description-broken-condition': problem.problemType === 'broken-condition'
                                }"
                              >
                                <span class="font-medium">{{ getProblemTypeLabel(problem.problemType) }} :</span>
                                {{ problem.description }}
                              </p>
                              
                              <!-- Détails pour les conditions cassées -->
                              <div v-if="problem.problemType === 'broken-condition' && problem.details" class="mt-2">
                                <div class="text-xs text-red-600 bg-red-50 p-2 rounded border">
                                  <strong>Conditions cassées :</strong>
                                  <ul class="mt-1 space-y-1">
                                    <li v-for="config in problem.details" :key="config.id" class="flex items-center">
                                      <span class="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                                      Condition vers section ID: {{ config.condition_id }} (introuvable)
                                    </li>
                                  </ul>
                                </div>
                              </div>
                              
                              <!-- Métadonnées -->
                              <div class="section-problem-meta">
                                <span 
                                  class="section-problem-meta-badge"
                                  :class="{
                                    'section-problem-meta-badge-orphaned': problem.problemType === 'orphaned',
                                    'section-problem-meta-badge-no-type': problem.problemType === 'no-type',
                                    'section-problem-meta-badge-broken-condition': problem.problemType === 'broken-condition'
                                  }"
                                >
                                  Type: {{ problem.section.type || 'Non défini' }}
                                </span>
                                <!-- Champs ordre et visible masqués pour simplifier l'interface -->
                              </div>
                            </div>
                          </div>
                          
                          <!-- Actions spécifiques au type de problème -->
                          <div class="section-problem-actions">
                            <!-- Actions pour sections orphelines -->
                            <template v-if="problem.problemType === 'orphaned'">
                              <button
                                class="section-problem-btn section-problem-btn-repair"
                                title="Créer les parents manquants"
                                @click="repairProblem(problem, 'create-parents')"
                              >
                                <span>🔨</span>
                                <span>Créer parents</span>
                              </button>
                              <button
                                class="section-problem-btn section-problem-btn-move"
                                title="Déplacer vers un autre parent"
                                @click="openMoveModal(problem)"
                              >
                                <span>📁</span>
                                <span>Déplacer</span>
                              </button>
                            </template>
                            
                            <!-- Actions pour sections sans type -->
                            <template v-if="problem.problemType === 'no-type'">
                              <button
                                class="section-problem-btn section-problem-btn-set-type"
                                title="Choisir un type pour cette section"
                                @click="openTypeSelectionModal(problem)"
                              >
                                <span>🏷️</span>
                                <span>Choisir type</span>
                              </button>
                            </template>
                            
                            <!-- Actions pour conditions cassées -->
                            <template v-if="problem.problemType === 'broken-condition'">
                              <button
                                class="section-problem-btn section-problem-btn-fix-conditions"
                                title="Supprimer les conditions cassées"
                                @click="repairProblem(problem, 'fix-conditions')"
                              >
                                <span>🔧</span>
                                <span>Réparer conditions</span>
                              </button>
                            </template>
                            
                            <!-- Action commune : supprimer -->
                            <button
                              class="section-problem-btn section-problem-btn-delete"
                              title="Supprimer cette section"
                              @click="repairProblem(problem, 'delete')"
                            >
                              <span>🗑️</span>
                              <span>Supprimer</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Actions globales -->
                  <div class="section-global-actions">
                    <h4 class="section-global-actions-title">Actions globales :</h4>
                    <div class="section-global-actions-buttons">
                      <button
                        v-if="sectionsStore.orphanedSections.length > 0"
                        class="section-global-btn section-global-btn-repair-all"
                        @click="repairAllProblems('create-parents')"
                      >
                        🔨 Créer tous les parents manquants ({{ sectionsStore.orphanedSections.length }})
                      </button>
                      <button
                        v-if="sectionsStore.sectionsWithoutType.length > 0"
                        class="section-global-btn section-global-btn-repair-all"
                        @click="openGlobalTypeSelectionModal"
                      >
                        🏷️ Définir types manquants ({{ sectionsStore.sectionsWithoutType.length }})
                      </button>
                      <button
                        v-if="sectionsStore.sectionsWithBrokenConditions.length > 0"
                        class="section-global-btn section-global-btn-repair-all"
                        @click="repairAllProblems('fix-conditions')"
                      >
                        🔧 Réparer conditions cassées ({{ sectionsStore.sectionsWithBrokenConditions.length }})
                      </button>
                      <button
                        class="section-global-btn section-global-btn-delete-all"
                        @click="repairAllProblems('delete')"
                      >
                        🗑️ Supprimer tous les problèmes ({{ sectionsStore.problemCount }})
                      </button>
                    </div>
                  </div>
                  
                  <!-- Formulaire de sélection de type -->
                  <div v-if="currentProblem && selectedRepairAction === 'set-type'" class="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-4">
                    <h4 class="font-medium text-purple-900 mb-4 text-lg">
                      🏷️ Choisir un type pour "{{ currentProblem.section.name }}"
                    </h4>
                    
                    <div class="mb-4">
                      <label class="section-form-label text-purple-800">
                        Sélectionnez le type de section :
                      </label>
                      <div class="grid grid-cols-2 gap-3 mt-2">
                        <label 
                          v-for="type in availableTypes" 
                          :key="type.value"
                          class="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
                          :class="selectedSectionType === type.value ? 'border-purple-500 bg-purple-100' : 'border-gray-300'"
                        >
                          <input
                            v-model="selectedSectionType"
                            type="radio"
                            :value="type.value"
                            class="sr-only"
                          />
                          <div class="flex items-center space-x-3">
                            <span class="text-2xl">{{ type.icon }}</span>
                            <div>
                              <div class="font-medium text-gray-900">{{ type.label }}</div>
                              <div class="text-sm text-gray-500">{{ type.description }}</div>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    <div v-if="selectedSectionType" class="mb-4 p-3 bg-purple-100 rounded-md">
                      <p class="text-sm text-purple-800">
                        <strong>Type sélectionné :</strong> 
                        <span class="font-mono">{{ getTypeLabel(selectedSectionType) }}</span>
                      </p>
                    </div>
                    
                    <div class="flex space-x-3">
                      <button
                        :disabled="!selectedSectionType"
                        class="section-problem-btn section-problem-btn-set-type disabled:bg-gray-400 disabled:cursor-not-allowed"
                        @click="repairProblem(currentProblem, 'set-type', selectedSectionType)"
                      >
                        <span>✅</span>
                        <span>Définir le type</span>
                      </button>
                      <button
                        class="section-problem-btn bg-gray-600 text-white hover:bg-gray-700"
                        @click="closeTypeSelectionModal"
                      >
                        <span>❌</span>
                        <span>Annuler</span>
                      </button>
                    </div>
                  </div>
                  
                  <!-- Formulaire de déplacement -->
                  <div v-if="currentProblem && selectedRepairAction === 'move'" class="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-4">
                    <h4 class="font-medium text-blue-900 mb-4 text-lg">
                      📁 Déplacer "{{ currentProblem.section.name }}" vers un nouveau parent
                    </h4>
                    
                    <div class="mb-4">
                      <label class="section-form-label text-blue-800">
                        Sélectionnez le nouveau parent :
                      </label>
                      <select 
                        v-model="selectedNewParent"
                        class="section-form-select border-blue-300 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Choisir un parent --</option>
                        <option value="ROOT">🏠 Racine (section de premier niveau)</option>
                        <optgroup label="📁 Sections conteneurs disponibles">
                          <option 
                            v-for="parent in availableParentSections"
                            :key="parent.value"
                            :value="parent.value"
                          >
                            {{ parent.fullPath }}
                          </option>
                        </optgroup>
                      </select>
                    </div>
                    
                    <div v-if="selectedNewParent" class="mb-4 p-3 bg-blue-100 rounded-md">
                      <p class="text-sm text-blue-800">
                        <strong>Aperçu :</strong> 
                        <span class="font-mono">
                          {{ currentProblem.section.name }} 
                          → 
                          {{ selectedNewParent === 'ROOT' 
                              ? SectionUtils.getLastSegment(currentProblem.section.name) 
                              : selectedNewParent + '.' + SectionUtils.getLastSegment(currentProblem.section.name) 
                          }}
                        </span>
                      </p>
                    </div>
                    
                    <div class="flex space-x-3">
                      <button
                        :disabled="!selectedNewParent"
                        class="section-problem-btn section-problem-btn-move disabled:bg-gray-400 disabled:cursor-not-allowed"
                        @click="repairProblem(currentProblem, 'move', selectedNewParent)"
                      >
                        <span>✅</span>
                        <span>Déplacer</span>
                      </button>
                      <button
                        class="section-problem-btn bg-gray-600 text-white hover:bg-gray-700"
                        @click="closeMoveModal"
                      >
                        <span>❌</span>
                        <span>Annuler</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Footer du modal -->
            <div class="section-modal-footer">
              <button
                class="section-modal-btn section-modal-btn-secondary"
                @click="closeOrphanModal"
              >
                Fermer
              </button>
              <button
                class="section-modal-btn section-modal-btn-primary"
                @click="refreshProblems"
              >
                🔄 Actualiser
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    
    <!-- Modal de confirmation pour actions globales -->
    <Teleport to="body">
      <div v-if="sectionsStore.showGlobalRepairModal" class="section-modal-overlay" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="section-modal-container">
          <div class="section-modal-content section-modal-content-sm">
            <div class="section-modal-header">
              <div class="flex items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10"
                  :class="globalRepairAction === 'delete' ? 'bg-red-100' : 'bg-blue-100'"
                >
                  <span class="text-2xl">
                    {{ getGlobalActionIcon(globalRepairAction) }}
                  </span>
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 class="section-modal-title">
                    {{ getGlobalActionTitle(globalRepairAction) }}
                  </h3>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500 mb-4">
                      {{ getGlobalActionDescription(globalRepairAction) }}
                    </p>
                    
                    <!-- Liste des sections concernées -->
                    <div class="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <h4 class="font-medium text-gray-700 mb-2">Sections concernées :</h4>
                      <ul class="space-y-1">
                        <li v-for="problem in getProblemsForAction(globalRepairAction)" :key="problem.section.id" class="text-sm text-gray-600 flex items-center">
                          <span class="w-2 h-2 rounded-full mr-2" :class="getProblemDotColor(problem.problemType)"></span>
                          {{ problem.section.name }} ({{ getProblemTypeLabel(problem.problemType) }})
                        </li>
                      </ul>
                    </div>
                    
                    <p class="text-sm text-gray-500 mt-4">
                      {{ getGlobalActionWarning(globalRepairAction) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Footer du modal de confirmation -->
            <div class="section-modal-footer">
              <button
                class="section-modal-btn"
                :class="globalRepairAction === 'delete' 
                  ? 'section-modal-btn-danger' 
                  : 'section-modal-btn-primary'"
                @click="confirmGlobalRepair"
              >
                {{ getGlobalActionConfirmText(globalRepairAction) }}
              </button>
              <button
                class="section-modal-btn section-modal-btn-cancel"
                @click="closeGlobalRepairModal"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    
    <!-- Modal de sélection globale de types -->
    <Teleport to="body">
      <div v-if="showGlobalTypeModal" class="section-modal-overlay" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="section-modal-container">
          <div class="section-modal-content section-modal-content-xl">
            <div class="section-modal-header">
              <h3 class="section-modal-title">
                🏷️ Définir les types pour {{ sectionsStore.sectionsWithoutType.length }} section(s)
              </h3>
            </div>
            
            <div class="section-modal-body">
              <div class="mb-4">
                <p class="text-sm text-gray-600 mb-4">
                  Sélectionnez le type approprié pour chaque section sans type défini :
                </p>
                
                <div class="space-y-4 max-h-96 overflow-y-auto">
                  <div 
                    v-for="section in sectionsStore.sectionsWithoutType" 
                    :key="section.id"
                    class="bg-gray-50 border border-gray-200 rounded-lg p-4"
                  >
                    <div class="flex items-start justify-between">
                      <div class="flex-1 min-w-0">
                        <h4 class="font-medium text-gray-900 truncate">{{ section.name }}</h4>
                        <p class="text-sm text-gray-500 mt-1">ID: {{ section.id }}</p>
                      </div>
                      
                      <div class="ml-4 flex-shrink-0">
                        <select 
                          v-model="selectedTypesForProblems[section.id.toString()]"
                          class="section-form-select text-sm"
                        >
                          <option value="">-- Choisir un type --</option>
                          <option 
                            v-for="type in availableTypes" 
                            :key="type.value"
                            :value="type.value"
                          >
                            {{ type.icon }} {{ type.label }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Footer du modal -->
            <div class="section-modal-footer">
              <button
                class="section-modal-btn section-modal-btn-cancel"
                @click="closeGlobalTypeSelectionModal"
              >
                Annuler
              </button>
              <button
                :disabled="Object.values(selectedTypesForProblems).some(type => !type)"
                class="section-modal-btn section-modal-btn-primary disabled:bg-gray-400 disabled:cursor-not-allowed"
                @click="confirmGlobalTypeSelection"
              >
                🏷️ Définir tous les types
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useSectionsStore } from '@/stores/sectionsStore';
import type { SectionExt, ProblemSection } from '@/stores/sectionsStore';
import { SectionUtils } from '@/utils/sectionUtils';
import type { SectionType } from '@/types/section';

export default defineComponent({
  name: 'OrphanManagement',
  emits: ['show-notification'],
  setup(_, { emit }) {
    const sectionsStore = useSectionsStore();

    // État local
    const currentProblem = ref<ProblemSection | null>(null);
    const selectedRepairAction = ref<string>('');
    const selectedNewParent = ref<string>('');
    const selectedSectionType = ref<string>('');
    const globalRepairAction = ref<'create-parents' | 'set-type' | 'fix-conditions' | 'delete' | null>(null);
    const showGlobalTypeModal = ref<boolean>(false);
    const selectedTypesForProblems = ref<Record<string, string>>({});
    
    // Types disponibles pour les sections
    const availableTypes: SectionType[] = [
      { value: 'text', label: 'Texte', description: 'Champ de saisie simple', icon: '📝' },
      { value: 'textarea', label: 'Zone de texte', description: 'Champ de saisie multi-lignes', icon: '📄' },
      { value: 'bool', label: 'Booléen', description: 'Case à cocher oui/non', icon: '☑️' },
      { value: 'collapse', label: 'Conteneur', description: 'Section pliable/dépliable', icon: '📁' },
      { value: 'dropdown', label: 'Liste déroulante', description: 'Sélection dans une liste', icon: '📋' }
    ];

    // Computed properties
    const availableParentSections = computed(() => {
      return sectionsStore.sections
        .filter(section => !isFinalSection(section))
        .map(section => ({
          value: section.name,
          label: section.name,
          displayName: SectionUtils.getLastSegment(section.name),
          fullPath: section.name
        }))
        .sort((a, b) => a.fullPath.localeCompare(b.fullPath));
    });

    // Méthodes utilitaires
    const isFinalSection = (section: SectionExt): boolean => {
      const sectionFullName = section.name;
      return !sectionsStore.sections.some(s => s.name.startsWith(sectionFullName + '.'));
    };

    const showNotification = (message: string, type: string): void => {
      emit('show-notification', message, type);
    };

    const getProblemTypeLabel = (problemType: string): string => {
      switch (problemType) {
        case 'orphaned':
          return 'Section orpheline';
        case 'no-type':
          return 'Type manquant';
        case 'broken-condition':
          return 'Condition cassée';
        default:
          return 'Problème inconnu';
      }
    };

    const getProblemDotColor = (problemType: string): string => {
      switch (problemType) {
        case 'orphaned':
          return 'bg-orange-400';
        case 'no-type':
          return 'bg-yellow-400';
        case 'broken-condition':
          return 'bg-red-400';
        default:
          return 'bg-gray-400';
      }
    };

    const getGlobalActionIcon = (action: string | null): string => {
      switch (action) {
        case 'create-parents':
          return '🔨';
        case 'set-type':
          return '🏷️';
        case 'fix-conditions':
          return '🔧';
        case 'delete':
          return '🗑️';
        default:
          return '❓';
      }
    };

    const getGlobalActionTitle = (action: string | null): string => {
      switch (action) {
        case 'create-parents':
          return 'Créer tous les parents manquants';
        case 'set-type':
          return 'Définir tous les types manquants';
        case 'fix-conditions':
          return 'Réparer toutes les conditions cassées';
        case 'delete':
          return 'Supprimer toutes les sections à problèmes';
        default:
          return 'Action inconnue';
      }
    };

    const getGlobalActionDescription = (action: string | null): string => {
      const count = getProblemsForAction(action).length;
      switch (action) {
        case 'create-parents':
          return `Vous êtes sur le point de créer automatiquement tous les parents manquants pour ${count} section(s) orpheline(s).`;
        case 'set-type':
          return `Vous êtes sur le point de définir un type par défaut (text) pour ${count} section(s) finale(s) sans type.`;
        case 'fix-conditions':
          return `Vous êtes sur le point de supprimer toutes les conditions cassées pour ${count} section(s).`;
        case 'delete':
          return `Vous êtes sur le point de supprimer ${count} section(s) à problèmes.`;
        default:
          return '';
      }
    };

    const getGlobalActionWarning = (action: string | null): string => {
      switch (action) {
        case 'create-parents':
          return '✅ Cette action va créer automatiquement toute la hiérarchie manquante.';
        case 'set-type':
          return '✅ Cette action va définir le type "text" par défaut pour toutes les sections concernées.';
        case 'fix-conditions':
          return '✅ Cette action va supprimer toutes les conditions qui pointent vers des sections inexistantes.';
        case 'delete':
          return '⚠️ Cette action ne peut pas être annulée.';
        default:
          return '';
      }
    };

    const getGlobalActionConfirmText = (action: string | null): string => {
      switch (action) {
        case 'create-parents':
          return '🔨 Créer tout';
        case 'set-type':
          return '🏷️ Définir tout';
        case 'fix-conditions':
          return '🔧 Réparer tout';
        case 'delete':
          return '🗑️ Supprimer tout';
        default:
          return 'Confirmer';
      }
    };

    const getProblemsForAction = (action: string | null): ProblemSection[] => {
      switch (action) {
        case 'create-parents':
          return sectionsStore.problemSections.filter(p => p.problemType === 'orphaned');
        case 'set-type':
          return sectionsStore.problemSections.filter(p => p.problemType === 'no-type');
        case 'fix-conditions':
          return sectionsStore.problemSections.filter(p => p.problemType === 'broken-condition');
        case 'delete':
          return sectionsStore.problemSections;
        default:
          return [];
      }
    };

    // Actions principales
    const openOrphanModal = (): void => {
      sectionsStore.showOrphanModal = true;
    };

    const closeOrphanModal = (): void => {
      sectionsStore.showOrphanModal = false;
      closeMoveModal();
    };

    const refreshProblems = async (): Promise<void> => {
      await sectionsStore.fetchSectionsData();
      showNotification("Liste des problèmes actualisée.", "info");
    };

    // Gestion des réparations individuelles
    const repairProblem = async (problem: ProblemSection, action: string, newParent?: string): Promise<void> => {
      try {
        const result = await sectionsStore.repairSection(problem, action, newParent);
        if (result.success) {
          const actionText = getActionSuccessText(action);
          showNotification(`${actionText} pour "${problem.section.name}".`, "success");
          
          // Fermer les modaux ouverts
          closeMoveModal();
          closeTypeSelectionModal();
          
          // Pas besoin de recharger, la réparation met déjà à jour localement
        } else {
          showNotification("Erreur lors de la réparation de la section.", "error");
        }
      } catch (error) {
        console.error('Erreur lors de la réparation de la section:', error);
        showNotification("Erreur lors de la réparation de la section.", "error");
      }
    };

    const getActionSuccessText = (action: string): string => {
      switch (action) {
        case 'create-parents':
          return 'Parents créés';
        case 'move':
          return 'Section déplacée';
        case 'set-type':
          return 'Type défini';
        case 'delete':
          return 'Section supprimée';
        default:
          return 'Action effectuée';
      }
    };

    // Gestion du déplacement
    const openMoveModal = (problem: ProblemSection): void => {
      currentProblem.value = problem;
      selectedRepairAction.value = 'move';
      selectedNewParent.value = '';
    };

    const closeMoveModal = (): void => {
      currentProblem.value = null;
      selectedRepairAction.value = '';
      selectedNewParent.value = '';
    };

    // Gestion de la sélection de type
    const openTypeSelectionModal = (problem: ProblemSection): void => {
      currentProblem.value = problem;
      selectedRepairAction.value = 'set-type';
      selectedSectionType.value = '';
    };

    const closeTypeSelectionModal = (): void => {
      currentProblem.value = null;
      selectedRepairAction.value = '';
      selectedSectionType.value = '';
    };

    const getTypeLabel = (typeValue: string): string => {
      const type = availableTypes.find(t => t.value === typeValue);
      return type ? type.label : typeValue;
    };

    // Gestion des actions globales
    const repairAllProblems = (action: 'create-parents' | 'set-type' | 'fix-conditions' | 'delete'): void => {
      const problemsForAction = getProblemsForAction(action);
      if (problemsForAction.length === 0) {
        showNotification("Aucun problème de ce type à réparer.", "info");
        return;
      }
      
      globalRepairAction.value = action;
      sectionsStore.showGlobalRepairModal = true;
    };

    // Gestion de la sélection globale de types
    const openGlobalTypeSelectionModal = (): void => {
      showGlobalTypeModal.value = true;
      // Initialiser les types sélectionnés avec le type par défaut
      const defaultSelections: Record<string, string> = {};
      sectionsStore.sectionsWithoutType.forEach(section => {
        defaultSelections[section.id] = 'text'; // Type par défaut
      });
      selectedTypesForProblems.value = defaultSelections;
    };

    const closeGlobalTypeSelectionModal = (): void => {
      showGlobalTypeModal.value = false;
      selectedTypesForProblems.value = {};
    };

    const confirmGlobalTypeSelection = async (): Promise<void> => {
      try {
        let successCount = 0;
        for (const [sectionId, selectedType] of Object.entries(selectedTypesForProblems.value)) {
          const section = sectionsStore.sectionsWithoutType.find(s => s.id.toString() === sectionId);
          if (section && selectedType) {
            const problem: ProblemSection = {
              section,
              problemType: 'no-type',
              description: 'Section sans type défini'
            };
            const result = await sectionsStore.repairSection(problem, 'set-type', selectedType);
            if (result.success) {
              successCount++;
            }
          }
        }
        
        showNotification(`${successCount} section(s) corrigée(s) avec succès.`, "success");
        
        // Actualiser automatiquement la liste des problèmes
        await sectionsStore.fetchSectionsData();
        
      } catch (error) {
        console.error('Erreur lors de la réparation globale des types:', error);
        showNotification("Erreur lors de la réparation globale des types.", "error");
      } finally {
        closeGlobalTypeSelectionModal();
      }
    };

    const closeGlobalRepairModal = (): void => {
      sectionsStore.showGlobalRepairModal = false;
      globalRepairAction.value = null;
    };

    const confirmGlobalRepair = async (): Promise<void> => {
      if (!globalRepairAction.value) return;
      
      const problemsToRepair = getProblemsForAction(globalRepairAction.value);
      const count = problemsToRepair.length;
      
      try {
        for (const problem of problemsToRepair) {
          await sectionsStore.repairSection(problem, globalRepairAction.value);
        }
        
        const actionText = getGlobalActionSuccessText(globalRepairAction.value);
        showNotification(`${count} section(s) ${actionText} avec succès.`, "success");
        
        // Actualiser automatiquement la liste des problèmes
        await sectionsStore.fetchSectionsData();
        
      } catch (error) {
        console.error('Erreur lors de la réparation globale:', error);
        showNotification("Erreur lors de la réparation globale des sections.", "error");
      } finally {
        closeGlobalRepairModal();
      }
    };

    const getGlobalActionSuccessText = (action: string): string => {
      switch (action) {
        case 'create-parents':
          return 'réparée(s)';
        case 'set-type':
          return 'corrigée(s)';
        case 'delete':
          return 'supprimée(s)';
        default:
          return 'traitée(s)';
      }
    };

    // Exposer les méthodes pour le composant parent
    const exposedMethods = {
      openOrphanModal,
    };

    return {
      sectionsStore,
      currentProblem,
      selectedRepairAction,
      selectedNewParent,
      selectedSectionType,
      globalRepairAction,
      showGlobalTypeModal,
      selectedTypesForProblems,
      
      // Types et données
      availableTypes,
      
      // Computed
      availableParentSections,
      
      // Méthodes utilitaires
      SectionUtils,
      getProblemTypeLabel,
      getProblemDotColor,
      getGlobalActionIcon,
      getGlobalActionTitle,
      getGlobalActionDescription,
      getGlobalActionWarning,
      getGlobalActionConfirmText,
      getProblemsForAction,
      getTypeLabel,
      
      // Actions principales
      closeOrphanModal,
      refreshProblems,
      
      // Gestion des réparations
      repairProblem,
      
      // Gestion du déplacement
      openMoveModal,
      closeMoveModal,
      
      // Gestion de la sélection de type
      openTypeSelectionModal,
      closeTypeSelectionModal,
      
      // Gestion des actions globales
      repairAllProblems,
      closeGlobalRepairModal,
      confirmGlobalRepair,
      
      // Gestion de la sélection globale de types
      openGlobalTypeSelectionModal,
      closeGlobalTypeSelectionModal,
      confirmGlobalTypeSelection,
      
      // Méthodes exposées
      ...exposedMethods,
    };
  }
});
</script>

<style scoped>
/* ===================================
   STYLES POUR LES MODAUX DE RÉPARATION
   ================================== */

/* Overlay du modal */
.section-modal-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease;
  backdrop-filter: blur(4px);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

/* Container du modal */
.section-modal-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  width: 100%;
}

/* Contenu du modal */
.section-modal-content {
  position: relative;
  transform: scale(1);
  overflow: hidden;
  border-radius: 0.5rem;
  background-color: white;
  text-align: left;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  transition: all 0.3s ease;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.section-modal-content-sm {
  max-width: 32rem;
}

.section-modal-content-xl {
  max-width: 64rem;
}

/* En-tête du modal */
.section-modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.section-modal-title {
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

/* Corps du modal */
.section-modal-body {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
}

/* Footer du modal */
.section-modal-footer {
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* Boutons du modal */
.section-modal-btn {
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.section-modal-btn-primary {
  background-color: #2563eb;
  color: white;
}

.section-modal-btn-primary:hover {
  background-color: #1d4ed8;
}

.section-modal-btn-secondary {
  background-color: #6b7280;
  color: white;
}

.section-modal-btn-secondary:hover {
  background-color: #4b5563;
}

.section-modal-btn-danger {
  background-color: #dc2626;
  color: white;
}

.section-modal-btn-danger:hover {
  background-color: #b91c1c;
}

.section-modal-btn-cancel {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.section-modal-btn-cancel:hover {
  background-color: #e5e7eb;
}

/* ===================================
   STYLES POUR LA GESTION DES PROBLÈMES
   ================================== */

/* État vide */
.section-empty {
  text-align: center;
  padding: 3rem 1rem;
}

.section-empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.section-empty-title {
  font-size: 1.25rem;
  line-height: 1.75rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

.section-empty-description {
  color: #6b7280;
  margin: 0;
}

/* Container des problèmes */
.section-problems-container {
  space-y: 1.5rem;
}

.section-problems-header {
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 0.5rem;
  padding: 1rem;
}

.section-problems-text {
  color: #0c4a6e;
  margin: 0;
}

/* Liste des problèmes */
.section-problems-list {
  margin-top: 1.5rem;
}

.section-problems-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: 1rem;
}

/* Cartes de problèmes */
.section-problem-card {
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid;
  transition: all 0.2s ease;
}

.section-problem-card-orphaned {
  background-color: #fff7ed;
  border-color: #fed7aa;
}

.section-problem-card-orphaned:hover {
  background-color: #ffedd5;
  border-color: #fdba74;
}

.section-problem-card-no-type {
  background-color: #fffbeb;
  border-color: #fde68a;
}

.section-problem-card-no-type:hover {
  background-color: #fef3c7;
  border-color: #fcd34d;
}

.section-problem-card-broken-condition {
  background-color: #fffbeb;
  border-color: #fde68a;
}

.section-problem-card-broken-condition:hover {
  background-color: #fef3c7;
  border-color: #fcd34d;
}

/* Titres des problèmes */
.section-problem-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.section-problem-title-orphaned {
  color: #ea580c;
}

.section-problem-title-no-type {
  color: #d97706;
}

.section-problem-title-broken-condition {
  color: #d97706;
}

/* Descriptions des problèmes */
.section-problem-description {
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin-bottom: 0.75rem;
}

.section-problem-description-orphaned {
  color: #9a3412;
}

.section-problem-description-no-type {
  color: #92400e;
}

.section-problem-description-broken-condition {
  color: #92400e;
}

/* Métadonnées des problèmes */
.section-problem-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.section-problem-meta-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
}

.section-problem-meta-badge-orphaned {
  background-color: #fed7aa;
  color: #9a3412;
}

.section-problem-meta-badge-no-type {
  background-color: #fde68a;
  color: #92400e;
}

.section-problem-meta-badge-broken-condition {
  background-color: #fde68a;
  color: #92400e;
}

/* Actions des problèmes */
.section-problem-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.section-problem-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.section-problem-btn-repair {
  background-color: #dcfce7;
  color: #166534;
}

.section-problem-btn-repair:hover {
  background-color: #bbf7d0;
  color: #14532d;
}

.section-problem-btn-move {
  background-color: #dbeafe;
  color: #1e40af;
}

.section-problem-btn-move:hover {
  background-color: #bfdbfe;
  color: #1e3a8a;
}

.section-problem-btn-set-type {
  background-color: #f3e8ff;
  color: #7c3aed;
}

.section-problem-btn-set-type:hover {
  background-color: #e9d5ff;
  color: #6b21a8;
}

.section-problem-btn-fix-conditions {
  background-color: #f3e8ff;
  color: #7c3aed;
}

.section-problem-btn-fix-conditions:hover {
  background-color: #e9d5ff;
  color: #6b21a8;
}

.section-problem-btn-delete {
  background-color: #fee2e2;
  color: #dc2626;
}

.section-problem-btn-delete:hover {
  background-color: #fecaca;
  color: #b91c1c;
}

/* Actions globales */
.section-global-actions {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.section-global-actions-title {
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
}

.section-global-actions-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.section-global-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.section-global-btn-repair-all {
  background-color: #2563eb;
  color: white;
}

.section-global-btn-repair-all:hover {
  background-color: #1d4ed8;
}

.section-global-btn-delete-all {
  background-color: #dc2626;
  color: white;
}

.section-global-btn-delete-all:hover {
  background-color: #b91c1c;
}

/* Formulaires */
.section-form-label {
  display: block;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.section-form-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: white;
}

.section-form-select:focus {
  outline: none;
  box-shadow: 0 0 0 2px #3b82f6;
  border-color: #3b82f6;
}

/* Responsive */
@media (max-width: 768px) {
  .section-modal-content-xl {
    max-width: 100%;
    margin: 0.5rem;
  }
  
  .section-problems-grid {
    grid-template-columns: 1fr;
  }
  
  .section-global-actions-buttons {
    flex-direction: column;
  }
}

/* Accessibilité */
@media (prefers-reduced-motion: reduce) {
  .section-modal-overlay,
  .section-modal-content,
  .section-problem-card,
  .section-problem-btn,
  .section-global-btn {
    transition: none;
  }
}
</style> 